import styled from 'styled-components';
import SideBar from './SideBar';
import { Element, scroller } from 'react-scroll';
import { useMediaQuery } from 'react-responsive';
import recurring_items_1 from '../../../../assets/images/recurring_items_1.png';
import recurring_items_2 from '../../../../assets/images/recurring_items_2.svg';
import recurring_items_3 from '../../../../assets/images/recurring_items_3.svg';
import recurring_items_4 from '../../../../assets/images/recurring_items_4.svg';
import recurring_items_5 from '../../../../assets/images/recurring_items_5.svg';
import recurring_items_6 from '../../../../assets/images/recurring_items_6.svg';
import recurring_items_7 from '../../../../assets/images/recurring_items_7.svg';
import recurring_items_8 from '../../../../assets/images/recurring_items_8.svg';
import recurring_items_9 from '../../../../assets/images/recurring_items_9.svg';
import recurring_items_10 from '../../../../assets/images/recurring_items_10.svg';
import recurring_items_11 from '../../../../assets/images/recurring_items_11.svg';

const Container = styled.div`
  display: flex;
  margin: 3rem 2rem 2rem 2rem;
  background-color: white;
  @media (max-width: 768px) {
    flex-direction: column;
    margin: 0;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgb(194, 194, 194);
  @media (max-width: 768px) {
    margin: 1.5rem;
  }
`;

const Hero = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 3rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgb(194, 194, 194);
  &:last-of-type {
    border-bottom: none;
  }
  @media (max-width: 768px) {
    margin: 1.5rem;
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SidebarSection = styled.div`
  width: 220px;
  @media (max-width: 768px) {
    width: unset;
    position: sticky;
    top: 0px;
    background-color: #ebeae6;
    border-bottom: 1px solid #ebeae6;
    backdrop-filter: blur(8px);
  }
`;

const Content = styled.div`
  width: 75%;
  padding: 0 2rem;
  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
  }
`;

const HeroTitle = styled.div`
  font-weight: 600;
  font-size: 32px;
`;

const Title = styled.div`
  font-weight: 500;
  font-size: 26px;
  margin-bottom: 2rem;
  margin-top: 2rem;
`;

const Text = styled.div`
  font-weight: 300;
  text-align: justify;
  line-height: 22px;
  padding-bottom: 1rem;
`;

interface ImageProps {
  width?: number;
}

const Image = styled.img<ImageProps>`
  width: ${(props) => (props.width ? `${props.width}%` : '65%')};
  border-radius: 5px;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.15);
  margin: 1.5rem auto;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const RecurringItemsHowToUseApp = () => {
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const scrollToElement = (element: string) => {
    scroller.scrollTo(element, {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
      offset: isMobile ? -240 : -70,
    });
  };
  return (
    <Container>
      <SidebarSection>
        <SideBar scrollToElement={scrollToElement} />
      </SidebarSection>
      <Content>
        <Header>
          <Hero>
            <HeroTitle>How To Use</HeroTitle>
          </Hero>
          <Text>
            The Recurring Items App helps you automatically create repeating items in your
            monday.com boards. Instead of duplicating tasks manually, you can set a recurrence
            pattern, choose who’s assigned, and even save templates for future use.
          </Text>
          <Text>
            This document provides instructions on utilizing the application and outlines its
            features in detail.
          </Text>
        </Header>
        <Section>
          <Element name="installation">
            <Title>Installation</Title>
            <Text>1. Go to monday.com app marketplace or click the button below:</Text>
            <Text>
              <a href="https://auth.monday.com/oauth2/authorize?client_id=6da359f780004fde9d7accd928214284&response_type=install">
                <img
                  alt="Add to monday.com"
                  height="42"
                  src="https://dapulse-res.cloudinary.com/image/upload/f_auto,q_auto/remote_mondaycom_static/uploads/Tal/4b5d9548-0598-436e-a5b6-9bc5f29ee1d9_Group12441.png"
                />
              </a>
            </Text>
            <Text>
              2. Search for <b>"Recurring Items"</b>
            </Text>
            <Text>
              3. Click <b>"Install app"</b> button
            </Text>
            <SectionContent>
              <Image src={recurring_items_1} width={80} alt="recurring_items_1" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="get-started">
            <SectionContent>
              <Title>Get started</Title>
              <Text>1. Open the app in Item View</Text>
              <Text>
                <ul>
                  <li>Select the item you want to repeat.</li>
                  <li>
                    Open the <b>Recurring Items</b> app from the item view.
                  </li>
                </ul>
              </Text>
              <Text>2. Set your recurrence pattern</Text>
              <Text>
                <ul>
                  <li>Choose how often the item repeats (daily, weekly, monthly, custom).</li>
                  <li>Select the start date.</li>
                  <li>Define when it ends (by date or after a number of repetitions).</li>
                </ul>
              </Text>
              <Image src={recurring_items_2} width={50} alt="recurring_items_2" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="define-recurrence">
            <SectionContent>
              <Title>Define the Recurrence</Title>
              <Text>The first step is to decide how often the task should repeat.</Text>
              <Text>
                You can set the frequency by choosing how many days, weeks, or months should pass
                between each occurrence. For example, you might want it to repeat every two weeks.
                If you’re working with weekly tasks, you can also select specific days, such as
                Monday, Wednesday, and Friday.
              </Text>
              <Text>
                Next, choose a start date for when the recurrence begins. Finally, decide when it
                should end. You can either set an exact date for the recurrence to stop or let it
                run for a specific number of repetitions.
              </Text>
              <Image src={recurring_items_3} width={50} alt="recurring_items_3" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="date-columns">
            <SectionContent>
              <Title>Update Date Columns</Title>
              <Text>
                If your original item has one or more date columns, you can tell the app which of
                those should automatically update with each recurrence.
              </Text>
              <Text>
                For instance, if the original item has a date of <b>October 5, 2025 at 1:00 PM</b>,
                and your recurrence is set to create items on{' '}
                <b>October 6, October 7, and October 8</b>, those new items will have their dates
                shifted to match — keeping the <b>same time</b> but updating the day to reflect the
                new schedule.
              </Text>
              <Text>
                This ensures that deadlines and timelines always stay accurate as new items are
                generated.
              </Text>
              <Image src={recurring_items_4} width={50} alt="recurring_items_4" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="target-group">
            <SectionContent>
              <Title>Choose a Target Group</Title>
              <Text>
                Next, select the <b>group</b> on your board where the recurring items should appear.
              </Text>
              <Text>
                You might want to keep them in the same group as the original item, or you can
                organize them into a separate group dedicated to recurring tasks.
              </Text>
              <Text>This keeps your board clean and makes it easy to track recurring work.</Text>
              <Image src={recurring_items_5} width={50} alt="recurring_items_5" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="custom-item-name">
            <SectionContent>
              <Title>Custom Item Name</Title>
              <Text>
                The Custom Item Name feature allows you to automatically generate meaningful names
                for your recurring items. Instead of having every new item use the same title as the
                original, you can define a custom naming rule that includes both static text and
                dynamic values pulled from your board.
              </Text>
              <Text>
                This is especially helpful for organizing recurring tasks by date, owner, project,
                or any other column value.
              </Text>
              <Image src={recurring_items_6} width={50} alt="recurring_items_6" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="assign-people">
            <SectionContent>
              <Title>Assign People</Title>
              <Text>
                You can decide who will be responsible for each new recurring item. The app offers
                three assignment methods:
              </Text>
              <Text>
                <ul>
                  <li>
                    <b>Same as original item</b> - Copies the same assignees from the item you
                    started with.
                  </li>
                  <li>
                    <b>Fixed people</b> - Lets you select one or more team members who will be
                    assigned to every recurring item.
                  </li>
                  <li>
                    <b>Rotate between people</b> - Assigns tasks to a list of people in order,
                    cycling through them each time a new item is created. This is perfect for
                    rotating responsibilities like weekly reports or on-call schedules.
                  </li>
                </ul>
              </Text>
              <Text>
                This feature makes sure that recurring work is always assigned correctly without
                manual updates.
              </Text>
              <Image src={recurring_items_7} width={50} alt="recurring_items_7" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="save-templates">
            <SectionContent>
              <Title>Save as a Template</Title>
              <Text>
                If you plan to use the same recurrence setup in the future, you can save it as a
                template.
              </Text>
              <Text>
                Give the template a descriptive name, like <b>“Weekly Sprint Prep”</b> or{' '}
                <b>“Monthly Team Review”</b>, so you can easily find it later. You can also set it
                as your default for the current board, making it even faster to use next time.
              </Text>
              <Image src={recurring_items_8} width={50} alt="recurring_items_8" />
              <Text>
                Templates can be managed directly from the <b>Templates</b> dropdown. From here, you
                can rename, delete, or set a default template with just a few clicks.
              </Text>
              <Image src={recurring_items_9} width={50} alt="recurring_items_9" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="preview-schedule">
            <SectionContent>
              <Title>Preview the Schedule</Title>
              <Text>
                Once your recurrence pattern is set, the app will display a calendar view showing
                exactly when new items will be created.
              </Text>
              <Text>
                Here, you can make adjustments before finalizing. If there’s a specific date you
                don’t need, simply remove it. If you want to add extra dates, you can do that too.
              </Text>
              <Text>
                This step gives you full control and helps prevent mistakes before items are
                created.
              </Text>
              <Image src={recurring_items_10} width={50} alt="recurring_items_10" />
            </SectionContent>
          </Element>
        </Section>
        <Section>
          <Element name="create-recurring-items">
            <SectionContent>
              <Title>Create Your Recurring Items</Title>
              <Text>
                After everything is set up, click <b>Create</b>. The app will instantly generate all
                the items according to your recurrence schedule, complete with updated dates,
                correct assignees, and organized placement in the selected group.
              </Text>
              <Text>You’re now ready to focus on work instead of manually duplicating tasks.</Text>
              <Image src={recurring_items_11} width={50} alt="recurring_items_11" />
            </SectionContent>
          </Element>
        </Section>
      </Content>
    </Container>
  );
};

export default RecurringItemsHowToUseApp;
